import { useContext, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Icon } from "@iconify/react/dist/iconify.js";
import { Block, CRRender, Inline } from "@/components/crRender";
import Checkbox from "@/components/formFields/Checkbox";
import moment from "moment";
import { toast } from "react-toastify";
import QuotationDetailsSkeleton from "./Skeleton";
import "./style.css";
import "./SkeletonStyle.css";
import { GetProviderQuotationResponseDetails } from "@/api/endpoints/Broker/BrokerRequest";
import DatePickerC from "@/components/formFields/DatePicker";
import useNavs from "@/hooks/useNavs";
import useForm from "@/hooks/useForm";

interface AddOn {
    id: number;
    name: string;
    description: string;
    price: number;
    isSelected?: boolean;
}

interface QuotationDetails {
    hospitalName: string;
    serviceName: string;
    price: number;
    availableFromDate: string;
    availableToDate: string;
    patientSelectedDate: string;
    pendingPaymentAmount: number;
    services: string[];
    addOns: AddOn[];
    refundPolicy: string;
    paymentTerms: string;
    paymentPercentage: number;
}

const QuotationDetailsPage = () => {
    
    const [quotationData, setQuotationData] = useState<QuotationDetails | null>(null);
    const [selectedAddOns, setSelectedAddOns] = useState<number[]>([]);
    const [agreedToTerms, setAgreedToTerms] = useState(false);
    const [loading, setLoadingState] = useState(true);
    const [paymentLoading, setPaymentLoading] = useState(false);
    const { params: { hospitlaOrderId }, navigate, state: { hospitalPrice } } = useNavs();

    const { t, i18n } = useTranslation();

    const [scheduledDate, setScheduledDate] = useState<Date | null>(null);

    const { formHandler } = useForm();
    
    useEffect(() => {
        setLoadingState(true);

        GetProviderQuotationResponseDetails(hospitlaOrderId).then((res: any) => {
            setQuotationData(res.data);
            setLoadingState(false);
        }).catch((err: any) => {
            toast.error(err.message);
            setLoadingState(false);
        });
    }, [hospitlaOrderId, i18n.language]);



    const handleAddOnToggle = (addOnId: number) => {
        setSelectedAddOns(prev =>
            prev.includes(addOnId)
                ? prev.filter(id => id !== addOnId)
                : [...prev, addOnId]
        );
    };

    const totalAddOns = useMemo(() => {
        if (!quotationData) return 0;
        return quotationData.addOns
            .filter(addon => selectedAddOns.includes(`${addon.id}`) || addon.isSelected)
            .reduce((sum, addon) => sum + addon.price, 0);
    }, [quotationData, selectedAddOns]);

    const totalPrice = useMemo(() => {
        if (!quotationData) return 0;
        return Number(quotationData?.price) + Number(totalAddOns);
    }, [quotationData, totalAddOns]);

    const downPayment = useMemo(() => {
        if (!quotationData) return 0;
        return Number(totalPrice) * (Number(quotationData?.paymentPercentage) / 100);
    }, [totalPrice, quotationData?.paymentPercentage]);



    const handleAcceptAndProceed = () => {
        /*     if (!agreedToTerms && !quotationData?.pendingPaymentAmount)
                return toast.error(t("Please agree to the refund and payment policies"));
    
            setPaymentLoading(true)
    
            if (!quotationData?.paidPrice) {
    
                AcceptOffer(hospitalId, {
                    date: formHandler.formValues.scheduledDate?.value,
                    addOnsIds: selectedAddOns || [],
                }).then((res: any) => {
    
                    handlePay(downPayment || quotationData?.pendingPaymentAmount)
                })
            } else {
                handlePay(quotationData?.pendingPaymentAmount || downPayment)
            } */
    }

    const handlePay = (amount: number) => {
        /*    setPaymentLoading(true)
           const data = {
               orderId: quotationData?.orderId,
               orderAmount: Number(amount.toFixed(2)),
               redirectToPage: Links.Medical.redirect(requestId),
           }
           RegisterOrder(data).then((res) => {
               setPaymentLoading(false)
               const redirectUrl = res.formUrl
               window.location.href = redirectUrl
           }).catch((err) => {
               console.log(err)
               setPaymentLoading(false)
           })
    */
    }



    if (loading) {
        return <QuotationDetailsSkeleton />;
    }

    return (
        <div className="quotation-details-page">
            {/* Back Link */}
            <div className="qd-back-link" onClick={() => navigate(-1)}>
                <Icon icon="mdi:arrow-left" width={16} />
                <span>{t("Back to Request Details")}</span>
            </div>

            {/* Header */}
            <h1 className="qd-title">{t("Quotation Response")}</h1>
            <p className="qd-subtitle">
                {t("From")} {quotationData?.providerName}
            </p>

            <div className="qd-layout">
                <div className="qd-left-column">
                    <div className="qd-card">
                        <h2 className="qd-card-title">
                            {t("Quotation Details for")} {quotationData?.serviceName}
                        </h2>

                        <div className="qd-details-row">
                            <div className="qd-detail-item">
                                <div className="qd-detail-label">{t("Base Quotation")}</div>
                                <div className="qd-detail-description">
                                    {t("Includes standard procedure and hospital stay.")}
                                </div>
                                <div className="qd-detail-value">${hospitalPrice}.00</div>
                            </div>

                            <div className="qd-detail-item">
                                <div className="qd-detail-label">
                                    <Icon icon="solar:calendar-date-bold" className="qd-icon" />
                                    {t("Scheduled Date")}
                                </div>
                                <div className="qd-detail-description">{t("Proposed treatment date.")}</div>
                                <div className="qd-detail-value row">
                                    <CRRender isRender={!quotationData?.patientSelectedDate}>
                                        <DatePickerC
                                            formHandler={formHandler}
                                            name="scheduledDate"
                                            className={"col-md-12"}
                                            isSingle dates={scheduledDate}
                                            minDate={moment(quotationData?.availableFromDate).toDate()}
                                            maxDate={moment(quotationData?.availableToDate).toDate()}
                                            Apply={date => setScheduledDate(date as Date)} />
                                    </CRRender>
                                    <Block isRender={!!scheduledDate || !!quotationData?.patientSelectedDate} className="qd-detail-value-container">
                                        <span className="qd-detail-value-label">{t("prefered date to  do operation")}</span> {" "}
                                        <span className="qd-detail-value-value">{moment(scheduledDate || quotationData?.patientSelectedDate).format("YYYY-MM-DD")}</span>
                                    </Block>
                                    <Block className="col-sm-12" isRender={!scheduledDate && !quotationData?.patientSelectedDate} >{t("Select your preferred date")}</Block>
                                </div>
                            </div>
                        </div>

                        <div className="qd-section">
                            <h3 className="qd-section-title">{t("Included in your package:")}</h3>
                            <ul className="qd-list row">
                                {quotationData?.packages?.map((service, index) => (
                                    <li className="col-md-6" key={index}><span className="qd-list-item-icon"><Icon icon="mdi:check" width={16} height={16} /></span> {service}</li>
                                ))}
                            </ul>
                        </div>


                        <div className="qd-section">
                            <h3 className="qd-section-title">{t("Optional Add-ons")}</h3>
                            <div className="qd-addons-grid">
                                {quotationData?.addOns?.map((addon) => (
                                    <div
                                        key={addon.id}
                                        className={`qd-addon-card ${selectedAddOns.includes(addon.id) ? "qd-addon-selected" : ""
                                            }`}
                                        onClick={() => handleAddOnToggle(`${addon.id}`)}
                                    >
                                        <div className="qd-addon-checkbox">
                                            <CRRender isRender={!addon?.isSelected}>

                                                <Checkbox
                                                    typeId={addon.id}
                                                    itemsList={selectedAddOns}
                                                    className="relative-checkbox"
                                                />
                                            </CRRender>
                                            <CRRender isRender={addon?.isSelected}>
                                                <Icon icon="fluent:checkmark-12-filled" width={20} height={20} />
                                            </CRRender>
                                        </div>
                                        <div className="qd-addon-content">
                                            <div className="qd-addon-header">
                                                <h4 className="qd-addon-name">{addon.name}</h4>
                                                <span className="qd-addon-price">${addon.price.toLocaleString()}.00</span>
                                            </div>
                                            <p className="qd-addon-description">{addon.description}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Refund Policy and Payment Terms */}
                    <div className="qd-policies-row">
                        <div className="qd-policy-card">
                            <h3 className="qd-policy-title">{t("Payment Terms")}</h3>
                            <ul className="qd-policy-list">
                                {quotationData?.paymentTerms}
                            </ul>
                        </div>

                        <div className="qd-policy-card">
                            <h3 className="qd-policy-title">{t("Refund Policy")}</h3>
                            <p className="qd-policy-list">
                                {quotationData?.refundPolicy}
                            </p>
                        </div>

                    </div>
                </div>

                {/* Right Column - Total Summary */}
                <div className="qd-right-column">
                    <div className="qd-summary-card">
                        <h2 className="qd-summary-title">{t("Total Summary")}</h2>

                        <div className="qd-summary-item">
                            <span className="qd-summary-label">{t("Base Quotation")}</span>
                            <span className="qd-summary-value">${hospitalPrice || 0}</span>
                        </div>

                        <div className="qd-summary-item">
                            <span className="qd-summary-label">{t("Add-ons")}</span>
                            <span className="qd-summary-value">${totalAddOns || 0}</span>
                        </div>

                        <div className="qd-summary-item qd-summary-total">
                            <span className="qd-summary-label">{t("Total Price")}</span>
                            <span className="qd-summary-value">${totalPrice || 0}</span>
                        </div>

                        <CRRender isRender={!quotationData?.paidPrice}>
                            <div className="qd-summary-item qd-summary-down-payment">
                                <span className="qd-summary-label">
                                    {t("Down Payment")} ({Number(quotationData?.paymentPercentage) || 0}%)
                                </span>
                                <span className="qd-summary-value">${downPayment || 0}</span>
                            </div>

                            {/* Agreement Checkbox */}
                            <div className="qd-agreement">
                                <label className="qd-agreement-label">
                                    <input
                                        type="checkbox"
                                        checked={agreedToTerms}
                                        onChange={(e) => setAgreedToTerms(e.target.checked)}
                                        className="qd-agreement-checkbox"
                                    />
                                    <span>{t("I have read and agree to the refund and payment policies.")}</span>
                                </label>
                            </div>

                            {/* Accept Button */}
                            <button
                                className="qd-accept-btn"
                                onClick={handleAcceptAndProceed}
                                disabled={!agreedToTerms || (!formHandler.formValues.scheduledDate?.value && !quotationData?.patientSelectedDate) || paymentLoading}
                            >
                                {paymentLoading ? t("loading") : t("Accept & Proceed")}
                            </button>
                        </CRRender>


                        <CRRender isRender={!!quotationData?.pendingPaymentAmount}>
                            <button
                                className="qd-accept-btn"
                                onClick={handleAcceptAndProceed}
                            >
                                {paymentLoading ? t("Proceeding to payment...") : `${t("Proceed to payment")} $ ${quotationData?.pendingPaymentAmount}`}
                            </button>
                        </CRRender>


                    </div>
                </div>
            </div>
        </div >
    );
};

export default QuotationDetailsPage;

